import React, { useEffect, useMemo, useState } from "react";
import {
  MW_NOWY_HANDLING_COLUMNS,
  buildCompanyDict,
  validateCell,
  validateRowCrossField,
} from "./schema";
import CompanyCell from "./CompanyCell";

// Pojedynczy wpis "Magazyn Warszawa - Nowy Handling". Te same pola i ta sama
// walidacja co w tabeli trybu "wiele wpisow", tylko ulozone jedno pod drugim.
//
// props:
//   value          - obiekt wpisu { <colKey>: string }
//   onChange(row)  - nowy stan wpisu
//   companies      - string[] nazw firm ze slownika Magazyn Warszawa
//   onAddCompany(name) => Promise<string>
//   onValidityChange({ valid, errorCount })

const fieldWrap = { marginBottom: 16 };

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 500,
  marginBottom: 4,
  color: "#333",
};

const baseInput = {
  border: "1px solid #ccc",
  borderRadius: 4,
  padding: "10px 12px",
  fontSize: 16,
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
};

const errStyle = { color: "#d32f2f", fontSize: 13, marginTop: 4 };

const MwNowyHandlingSingle = ({
  value = {},
  onChange,
  companies = [],
  onAddCompany,
  onValidityChange,
}) => {
  const [touched, setTouched] = useState(() => new Set());
  const companyDict = useMemo(() => buildCompanyDict(companies), [companies]);

  const setField = (key, v) => onChange({ ...value, [key]: v });
  const markTouched = (key) =>
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  const errors = useMemo(() => {
    const out = {};
    MW_NOWY_HANDLING_COLUMNS.forEach((col) => {
      const res = validateCell(col, value[col.key], { companyDict });
      if (res && !(res.silent && res.fix != null)) out[col.key] = res;
    });
    Object.entries(validateRowCrossField(value)).forEach(([key, res]) => {
      if (!out[key]) out[key] = res;
    });
    return out;
  }, [value, companyDict]);

  useEffect(() => {
    if (typeof onValidityChange === "function") {
      const errorCount = Object.keys(errors).length;
      onValidityChange({ valid: errorCount === 0, errorCount });
    }
  }, [errors, onValidityChange]);

  const handleCompanyBlur = (col) => {
    markTouched(col.key);
    const res = validateCell(col, value[col.key], { companyDict });
    if (res && res.silent && res.fix != null) setField(col.key, res.fix);
  };

  return (
    <div>
      {MW_NOWY_HANDLING_COLUMNS.map((col) => {
        const showErr = touched.has(col.key) && Boolean(errors[col.key]);
        const errMsg = showErr ? errors[col.key].message : "";
        const inputStyle = {
          ...baseInput,
          borderColor: showErr ? "#d32f2f" : "#ccc",
          background: showErr ? "#fff5f5" : "#fff",
        };

        return (
          <div key={col.key} style={fieldWrap}>
            <label style={labelStyle}>
              {col.label} <span style={{ color: "#d32f2f" }}>*</span>
            </label>

            {col.type === "company" ? (
              <CompanyCell
                value={value[col.key] ?? ""}
                companies={companies}
                dict={companyDict}
                hasError={showErr}
                errorMessage={errMsg || undefined}
                onChange={(v) => setField(col.key, v)}
                onCommit={(v) => {
                  setField(col.key, v);
                  markTouched(col.key);
                }}
                onBlurCommit={() => handleCompanyBlur(col)}
                onAddCompany={onAddCompany}
              />
            ) : col.type === "time" ? (
              <input
                type="time"
                value={value[col.key] ?? ""}
                style={inputStyle}
                onChange={(e) => setField(col.key, e.target.value)}
                onBlur={() => markTouched(col.key)}
              />
            ) : (
              <input
                type="text"
                maxLength={col.exactLength || undefined}
                value={value[col.key] ?? ""}
                style={inputStyle}
                onChange={(e) => setField(col.key, e.target.value)}
                onBlur={() => markTouched(col.key)}
              />
            )}

            {showErr && <div style={errStyle}>{errMsg}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default MwNowyHandlingSingle;
